import { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import Modal from "react-bootstrap/Modal";
import { Button } from "react-bootstrap";
import Select from "react-select";
import {
  saveEegSegment,
  getEegLabelCategories,
} from "../../actions/patientActions";

const SaveSegmentModal = ({
  show,
  handleClose,
  token,
  segmentData,
  onSaved,
}) => {
  const [loading, setLoading] = useState(false);
  const [labelCategories, setLabelCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedCategory1, setSelectedCategory1] = useState(null);
  const [selectedCategory2, setSelectedCategory2] = useState(null);
  const [selectedLabel1, setSelectedLabel1] = useState(null);
  const [selectedLabel2, setSelectedLabel2] = useState(null);
  const dispatch = useDispatch();

  const fetchLabelCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const response = await dispatch(getEegLabelCategories(token));
      if (response?.data) {
        setLabelCategories(response.data);
      }
    } catch (error) {
      toast.error("Failed to load label categories", {
        position: "top-right",
      });
    } finally {
      setLoadingCategories(false);
    }
  }, [dispatch, token]);

  useEffect(() => {
    if (show && token) {
      fetchLabelCategories();
    }
  }, [show, token, fetchLabelCategories]);

  const categories = useMemo(() => {
    const uniqueCategories = [
      ...new Set(labelCategories.map((item) => item.category)),
    ];
    return uniqueCategories.map((category) => ({
      value: category,
      label: category,
    }));
  }, [labelCategories]);

  const labelsForCategory1 = useMemo(() => {
    if (!selectedCategory1) return [];
    return labelCategories
      .filter(
        (item) => item.category === selectedCategory1.value && item.is_active,
      )
      .map((item) => ({
        value: item.id,
        label: item.label_name,
        description: item.description,
      }));
  }, [labelCategories, selectedCategory1]);

  const labelsForCategory2 = useMemo(() => {
    if (!selectedCategory2) return [];
    return labelCategories
      .filter(
        (item) => item.category === selectedCategory2.value && item.is_active,
      )
      .map((item) => ({
        value: item.id,
        label: item.label_name,
        description: item.description,
      }));
  }, [labelCategories, selectedCategory2]);

  const handleCategory1Change = (selectedOption) => {
    setSelectedCategory1(selectedOption);
    setSelectedLabel1(null);
  };

  const handleCategory2Change = (selectedOption) => {
    setSelectedCategory2(selectedOption);
    setSelectedLabel2(null);
  };

  const handleConfirm = async () => {
    if (!segmentData) {
      toast.error("No segment data to save");
      return;
    }

    if (!selectedLabel1 || !selectedLabel2) {
      toast.error("Please select labels for both channels");
      return;
    }

    setLoading(true);
    try {
      await dispatch(
        saveEegSegment(token, {
          ...segmentData,
          ch1_label_id: selectedLabel1.value,
          ch2_label_id: selectedLabel2.value,
        }),
      );
      setSelectedCategory1(null);
      setSelectedCategory2(null);
      setSelectedLabel1(null);
      setSelectedLabel2(null);
      onSaved && onSaved();
      handleClose();
    } catch (error) {
      // Error toast is handled in the action
    } finally {
      setLoading(false);
    }
  };
  const reactSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "38px",
      boxShadow: "none",
      borderColor: "#ccc",
      "&:hover": {
        borderColor: "#ccc",
        cursor: "pointer",
      },
    }),

    input: (base) => ({
      ...base,
      boxShadow: "none",
      outline: "none",
      border: "none",
    }),

    valueContainer: (base) => ({
      ...base,
      cursor: "text",
    }),

    indicatorsContainer: (base) => ({
      ...base,
      cursor: "pointer",
    }),

    option: (base) => ({
      ...base,
      cursor: "pointer",
    }),
  };
  return (
    <Modal
      show={show}
      onHide={handleClose}
      animation={false}
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton className="modal-header">
        <Modal.Title id="contained-modal-title-vcenter">
          Tag EEG Segment
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ marginBottom: "20px" }}>
          <p>
            <strong>Time Range:</strong>{" "}
            {segmentData?.segment_start_time?.toFixed(2)}s -{" "}
            {segmentData?.segment_end_time?.toFixed(2)}s
          </p>
          <p>
            <strong>Duration:</strong>{" "}
            {(
              (segmentData?.segment_end_time || 0) -
              (segmentData?.segment_start_time || 0)
            ).toFixed(2)}
            s
          </p>
        </div>
        <div style={{ marginBottom: "20px" }}>
          <label style={{ marginBottom: "10px", display: "block" }}>
            <strong>Select Category for Channel 1:</strong>
          </label>
          <Select
            classNamePrefix="react-select"
            value={selectedCategory1}
            onChange={handleCategory1Change}
            options={categories}
            isSearchable
            placeholder="Select category..."
            isLoading={loadingCategories}
            styles={reactSelectStyles}
          />
        </div>
        <div style={{ marginBottom: "20px" }}>
          <label style={{ marginBottom: "10px", display: "block" }}>
            <strong>Select Label for Channel 1:</strong>
          </label>
          <Select
            classNamePrefix="react-select"
            value={selectedLabel1}
            onChange={setSelectedLabel1}
            options={labelsForCategory1}
            isSearchable
            placeholder={
              selectedCategory1
                ? "Select label..."
                : "Please select a category first"
            }
            isDisabled={!selectedCategory1}
            formatOptionLabel={({ label, description }) => (
              <div>
                <div style={{ fontWeight: 500 }}>{label}</div>
                {description && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "2px",
                    }}
                  >
                    {description}
                  </div>
                )}
              </div>
            )}
            styles={reactSelectStyles}
          />
        </div>
        <div style={{ marginBottom: "20px" }}>
          <label style={{ marginBottom: "10px", display: "block" }}>
            <strong>Select Category for Channel 2:</strong>
          </label>
          <Select
            classNamePrefix="react-select"
            value={selectedCategory2}
            onChange={handleCategory2Change}
            options={categories}
            isSearchable
            placeholder="Select category..."
            isLoading={loadingCategories}
            styles={reactSelectStyles}
          />
        </div>
        <div style={{ marginBottom: "20px" }}>
          <label style={{ marginBottom: "10px", display: "block" }}>
            <strong>Select Label for Channel 2:</strong>
          </label>
          <Select
            classNamePrefix="react-select"
            value={selectedLabel2}
            onChange={setSelectedLabel2}
            options={labelsForCategory2}
            isSearchable
            placeholder={
              selectedCategory2
                ? "Select label..."
                : "Please select a category first"
            }
            isDisabled={!selectedCategory2}
            formatOptionLabel={({ label, description }) => (
              <div>
                <div style={{ fontWeight: 500 }}>{label}</div>
                {description && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "2px",
                    }}
                  >
                    {description}
                  </div>
                )}
              </div>
            )}
            styles={reactSelectStyles}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="success" onClick={handleConfirm} disabled={loading}>
          {loading ? "Saving..." : "Save Segment"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SaveSegmentModal;
